import React, { useState, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  ChefHat,
  PawPrint,
  Sparkles,
  Dumbbell,
  Boxes,
  Tag,
  Handshake,
  ShoppingCart,
  ShieldCheck,
  TrendingUp,
  Globe,
  BadgeCheck,
  Truck,
  Warehouse,
  PackageCheck,
  Users,
  Languages,
} from "lucide-react";
import Marquee from "react-fast-marquee";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT = {
  phone: "+1 (305) 775-7289",
  whatsapp: "13057757289",
  email: "info@wondbox.com",
  city_es: "Miami, Florida, EE.UU.",
  city_en: "Miami, Florida, USA",
};

/* ---------------- i18n ---------------- */
const TRANSLATIONS = {
  es: {
    nav: { home: "Inicio", about: "Nosotros", products: "Productos", services: "Servicios", contact: "Contacto", cta: "Contáctanos" },
    hero: {
      badge: "Distribución multicanal",
      title1: "Tu producto.",
      title2: "Nuestro mercado.",
      subtitle: "Distribución confiable de productos de alta calidad.",
      body: "En Wonderbox nos aliamos con marcas y proveedores para llevar productos de calidad a nuestros clientes a través de una red de distribución eficiente y confiable.",
      cta: "Contáctanos",
      cta2: "Ver categorías",
      stats: [
        { k: "500+", v: "SKUs activos" },
        { k: "20+", v: "Categorías" },
        { k: "99%", v: "On-time" },
      ],
      chip1: "Confianza",
      chip1_sub: "Aliado de largo plazo",
      chip2: "Distribución",
      chip2_sub: "Red confiable y con alcance",
    },
    partners: "Los valores que nos definen",
    about: {
      kicker: "Sobre nosotros",
      title: "Distribución que hace crecer marcas.",
      p1: "Wonderbox es un distribuidor y exportador de confianza de productos de alta calidad, atendiendo clientes a nivel local, nacional y en toda Latinoamérica. Trabajamos con marcas y proveedores reconocidos para construir alianzas de largo plazo, mutuamente beneficiosas, basadas en confianza, fiabilidad y crecimiento constante.",
      p2: "Nuestro compromiso es representar a nuestros aliados con integridad, garantizar una distribución eficiente y ofrecer productos y servicio excepcionales a nuestros clientes. Siempre estamos buscando establecer relaciones sólidas con fabricantes y proveedores que compartan nuestro compromiso con la calidad y la excelencia.",
      hq: "Sede",
      email_l: "Email",
      phone_l: "Teléfono",
    },
    categories: {
      kicker: "Categorías que distribuimos",
      title: "Nuestro portafolio de productos.",
      note: "* Estamos abiertos a evaluar nuevas categorías rentables. Cuéntanos qué distribuyes.",
      active: "Distribución activa",
      items: [
        { key: "hogar", label: "Hogar y Cocina" },
        { key: "mascotas", label: "Mascotas" },
        { key: "belleza", label: "Belleza y Cuidado Personal" },
        { key: "fitness", label: "Fitness y Deporte" },
        { key: "organizacion", label: "Organización del Hogar" },
        { key: "mas", label: "Y más categorías" },
      ],
    },
    services: {
      kicker: "¿Por qué Wonderbox?",
      title: "Servicios diseñados para escalar tu marca.",
      items: [
        { title: "Aliado confiable", desc: "Comunicación clara y relaciones de largo plazo con cada proveedor." },
        { title: "Amplia red de distribución", desc: "Llevamos tus productos a nuestros canales de distribución con cobertura extensa." },
        { title: "Profesionalismo", desc: "Operamos con integridad, transparencia y compromiso total." },
        { title: "Crecemos juntos", desc: "Nuestro objetivo es escalar tu marca junto con nuestro negocio." },
      ],
    },
    contact: {
      kicker: "Trabajemos juntos",
      title: "¿Marca o distribuidor? Hablemos.",
      body: "Si tienes productos que quieres llevar al siguiente nivel a través de una red de distribución confiable, cuéntanos sobre tu catálogo. Te responderemos lo antes posible.",
      badges: ["Alcance Global", "Calidad Verificada", "Alianzas Win-Win"],
      form: {
        name: "Nombre completo *",
        name_ph: "Tu nombre",
        company: "Empresa",
        company_ph: "Nombre de tu empresa",
        email: "Email *",
        email_ph: "tu@correo.com",
        phone: "Teléfono",
        phone_ph: "+52 55 ...",
        country: "País",
        country_ph: "México, USA, Colombia...",
        message: "Cuéntanos sobre tus productos *",
        message_ph: "Categorías, volumen mensual estimado, mercados de interés...",
        submit: "Enviar mensaje",
        sending: "Enviando...",
        privacy: "Al enviar aceptas nuestro tratamiento responsable de datos. No compartimos información.",
      },
      toast_ok: "¡Mensaje enviado! Nos pondremos en contacto pronto.",
      toast_missing: "Por favor completa nombre, email y mensaje.",
      toast_err: "No se pudo enviar el mensaje. Verifica tus datos e inténtalo de nuevo.",
    },
    footer: { nav: "Navegación", contact: "Contacto", rights: "Todos los derechos reservados.", tagline: "Hecho con cuidado en" },
  },
  en: {
    nav: { home: "Home", about: "About Us", products: "Categories", services: "Services", contact: "Contact", cta: "Contact us" },
    hero: {
      badge: "Multi-channel distribution",
      title1: "Your product.",
      title2: "Our market.",
      subtitle: "Trusted distribution of high-quality products.",
      body: "At Wonderbox we partner with brands and suppliers to bring quality products to our customers through an efficient and reliable distribution network.",
      cta: "Contact us",
      cta2: "See categories",
      stats: [
        { k: "500+", v: "Active SKUs" },
        { k: "20+", v: "Categories" },
        { k: "99%", v: "On-time" },
      ],
      chip1: "Trust",
      chip1_sub: "Long-term partner",
      chip2: "Distribution",
      chip2_sub: "Reliable and far-reaching network",
    },
    partners: "The values that define us",
    about: {
      kicker: "About Us",
      title: "Distribution that grows brands.",
      p1: "Wonderbox is a trusted distributor and exporter of high-quality products, serving customers locally, nationwide, and across Latin America. We work with reputable brands and suppliers to build long-term, mutually beneficial partnerships based on trust, reliability, and consistent growth.",
      p2: "Our commitment is to represent our partners with integrity, ensure efficient distribution, and deliver exceptional products and service to our customers. We are always looking to establish strong relationships with manufacturers and suppliers who share our commitment to quality and excellence.",
      hq: "Headquarters",
      email_l: "Email",
      phone_l: "Phone",
    },
    categories: {
      kicker: "Categories we distribute",
      title: "Our Product Portfolio.",
      note: "* We're open to evaluating other profitable categories. Tell us what you distribute.",
      active: "Active distribution",
      items: [
        { key: "hogar", label: "Home & Kitchen" },
        { key: "mascotas", label: "Pet Supplies" },
        { key: "belleza", label: "Beauty & Personal Care" },
        { key: "fitness", label: "Fitness & Sports" },
        { key: "organizacion", label: "Home Organization" },
        { key: "mas", label: "And more categories" },
      ],
    },
    services: {
      kicker: "Why Wonderbox?",
      title: "Services designed to scale your brand.",
      items: [
        { title: "Reliable partner", desc: "Clear communication and long-term relationships with every supplier." },
        { title: "Wide distribution network", desc: "We take your products to our distribution channels with broad coverage." },
        { title: "Trusted & professional", desc: "We operate with integrity, transparency and full commitment." },
        { title: "We grow together", desc: "Our goal is to scale your brand alongside our business." },
      ],
    },
    contact: {
      kicker: "Let's work together",
      title: "Brand or distributor? Let's talk.",
      body: "If you have products you want to take to the next level through a reliable distribution network, tell us about your catalog. We'll get back to you as soon as possible.",
      badges: ["Global Sourcing", "Quality Products", "Win-Win Partnerships"],
      form: {
        name: "Full name *",
        name_ph: "Your name",
        company: "Company",
        company_ph: "Your company name",
        email: "Email *",
        email_ph: "you@email.com",
        phone: "Phone",
        phone_ph: "+1 555 ...",
        country: "Country",
        country_ph: "USA, Mexico, Colombia...",
        message: "Tell us about your products *",
        message_ph: "Categories, monthly volume, target markets...",
        submit: "Submit message",
        sending: "Sending...",
        privacy: "By submitting you accept our responsible data handling. We don't share your info.",
      },
      toast_ok: "Message sent! We'll get in touch shortly.",
      toast_missing: "Please complete name, email and message.",
      toast_err: "We couldn't send your message. Please check your data and try again.",
    },
    footer: { nav: "Navigation", contact: "Contact", rights: "All rights reserved.", tagline: "Crafted with care in" },
  },
};

/* ---------------- Logo ---------------- */
const Logo = ({ variant = "dark", className = "" }) => {
  // variant: "dark" (navy wordmark, for light bg) | "white" (white wordmark, for dark bg)
  const wordSrc = variant === "white" ? "/brand/wonderbox-wordmark-white.png?v=1" : "/brand/wonderbox-wordmark.png?v=1";
  return (
    <a href="#inicio" data-testid="nav-logo" className={`flex items-center gap-2.5 ${className}`}>
      <img
        src="/brand/wonderbox-icon.png?v=4"
        alt="Wonderbox"
        className="h-12 w-12 object-contain"
        draggable="false"
      />
      <img
        src={wordSrc}
        alt="wonderbox"
        className="h-6 w-auto object-contain sm:h-7"
        draggable="false"
      />
    </a>
  );
};

/* ---------------- Header ---------------- */
const Header = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const NAV_LINKS = [
    { label: t.nav.home, href: "#inicio" },
    { label: t.nav.about, href: "#nosotros" },
    { label: t.nav.services, href: "#servicios" },
  ];
  return (
    <header data-testid="nav-bar" className="sticky top-0 z-50 w-full border-b border-brand-100/70 bg-white/85 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Logo />
        <nav data-testid="nav-links" className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l, i) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${i}`} className="text-sm font-medium text-brand-900/80 transition-colors hover:text-brand-500">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangToggle lang={lang} setLang={setLang} />
          <a href="#contacto" data-testid="nav-cta" className="group hidden items-center gap-2 rounded-full bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-500/25 transition-all duration-300 hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/40 lg:inline-flex">
            {t.nav.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(v => !v)} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-brand-100 text-brand-700 lg:hidden" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-brand-100/70 bg-white lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="rounded-lg px-3 py-3 text-sm font-medium text-brand-900 hover:bg-brand-50">{l.label}</a>
            ))}
            <a href="#contacto" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-5 py-3 text-sm font-semibold text-white">
              {t.nav.cta} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const LangToggle = ({ lang, setLang }) => (
  <div data-testid="lang-toggle" className="inline-flex items-center rounded-full border border-brand-100 bg-white p-0.5">
    {["es", "en"].map((code) => (
      <button
        key={code}
        data-testid={`lang-${code}`}
        onClick={() => setLang(code)}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${lang === code ? "bg-brand-500 text-white shadow-sm" : "text-brand-900/60 hover:text-brand-700"}`}
      >
        {code === "es" && lang !== code && <Languages className="h-3 w-3" />}
        {code}
      </button>
    ))}
  </div>
);

/* ---------------- Hero ---------------- */
const Hero = ({ t }) => (
  <section id="inicio" data-testid="hero-section" className="relative overflow-hidden bg-white">
    <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-100/70 blur-3xl" />
    <div className="pointer-events-none absolute right-0 top-40 h-72 w-72 rounded-full bg-brand-50 blur-3xl" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 py-20 sm:px-10 lg:grid-cols-2 lg:gap-20 lg:py-28">
      <div className="animate-fade-up">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-700">{t.hero.badge}</span>
        </div>
        <h1 data-testid="hero-heading" className="font-display text-5xl font-black leading-[0.95] tracking-tight text-brand-900 sm:text-6xl lg:text-7xl">
          {t.hero.title1}<br />
          <span className="bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">{t.hero.title2}</span>
        </h1>
        <p className="mt-4 max-w-lg text-base font-semibold text-brand-500 sm:text-lg">{t.hero.subtitle}</p>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-brand-900/70 sm:text-lg">{t.hero.body}</p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <a href="#contacto" data-testid="hero-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition-all duration-300 hover:bg-brand-600 hover:shadow-xl hover:shadow-brand-500/40">
            <Mail className="h-4 w-4" /> {t.hero.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a href="#productos" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-200 bg-white px-7 py-3.5 text-sm font-semibold text-brand-900 transition-all duration-300 hover:border-brand-500 hover:bg-brand-50">
            {t.hero.cta2}
          </a>
        </div>
      </div>

      <div className="relative animate-fade-up">
        <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-brand-100/80 to-transparent" />
        <div className="relative overflow-hidden rounded-[1.75rem] border border-brand-100 bg-white shadow-2xl shadow-brand-900/10">
          <img src="https://images.pexels.com/photos/4498136/pexels-photo-4498136.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Wonderbox distribution products" className="h-[26rem] w-full object-cover sm:h-[32rem]" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-brand-900/90 px-4 py-3 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500/25">
                <Truck className="h-4 w-4 text-brand-200" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand-300">{t.hero.chip2}</div>
                <div className="text-sm font-semibold text-white">{t.hero.chip2_sub}</div>
              </div>
            </div>
            <BadgeCheck className="h-6 w-6 text-brand-300" />
          </div>
        </div>
        <div className="absolute -left-4 top-8 hidden rotate-[-4deg] rounded-2xl border border-brand-100 bg-white p-4 shadow-xl shadow-brand-500/10 sm:block animate-float">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50">
              <PackageCheck className="h-5 w-5 text-brand-500" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-900/50">{t.hero.chip1}</div>
              <div className="text-sm font-semibold text-brand-900">{t.hero.chip1_sub}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Partners Marquee ---------------- */
const Partners = ({ t, lang }) => {
  const values = lang === "es"
    ? ["CALIDAD", "CONFIANZA", "COMPROMISO", "RAPIDEZ", "ESCALABILIDAD", "ALIANZA", "EXCELENCIA", "INNOVACIÓN"]
    : ["QUALITY", "TRUST", "COMMITMENT", "SPEED", "SCALABILITY", "PARTNERSHIP", "EXCELLENCE", "INNOVATION"];
  return (
    <section data-testid="partners-marquee" className="border-y border-brand-100 bg-brand-50/60 py-10">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <p className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.3em] text-brand-900/50">{t.partners}</p>
        <Marquee gradient gradientColor="rgb(248, 244, 255)" speed={40} pauseOnHover>
          {values.map((b) => (
            <span key={b} className="mx-10 font-display text-2xl font-black tracking-tight text-brand-900/25 transition-colors hover:text-brand-500 sm:text-3xl">{b}</span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

/* ---------------- About + Categories ---------------- */
const CATEGORY_ICONS = { hogar: ChefHat, mascotas: PawPrint, belleza: Sparkles, fitness: Dumbbell, organizacion: Boxes, mas: Tag };

const AboutAndCategories = ({ lang, t }) => (
  <section id="nosotros" className="bg-white py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-10 lg:grid-cols-5">
      <div data-testid="about-section" className="lg:col-span-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-500">{t.about.kicker}</p>
        <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight text-brand-900 sm:text-5xl">{t.about.title}</h2>
        <p className="mt-6 text-base leading-relaxed text-brand-900/70 sm:text-lg">{t.about.p1}</p>
        <p className="mt-4 text-base leading-relaxed text-brand-900/70 sm:text-lg">{t.about.p2}</p>

        <div className="mt-10 space-y-4">
          <ContactRow icon={MapPin} label={t.about.hq} value={lang === "es" ? CONTACT.city_es : CONTACT.city_en} />
          <ContactRow icon={Mail} label={t.about.email_l} value={CONTACT.email} href={`mailto:${CONTACT.email}`} testId="contact-email-link" />
          <ContactRow icon={Phone} label={t.about.phone_l} value={CONTACT.phone} href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} testId="contact-phone-link" />
        </div>
      </div>

      <div id="productos" data-testid="products-section" className="lg:col-span-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-500">{t.categories.kicker}</p>
        <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight text-brand-900 sm:text-5xl">{t.categories.title}</h2>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {t.categories.items.map(({ key, label }) => {
            const Icon = CATEGORY_ICONS[key];
            return (
              <div key={key} data-testid={`category-card-${key}`} className="group relative overflow-hidden rounded-2xl border border-brand-100 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-300 hover:shadow-xl hover:shadow-brand-500/10">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-500 transition-all duration-300 group-hover:bg-brand-500 group-hover:text-white">
                  <Icon className="h-6 w-6" strokeWidth={1.9} />
                </div>
                <div className="text-sm font-semibold leading-snug text-brand-900">{label}</div>
                <div className="mt-1 text-xs text-brand-900/50">{t.categories.active}</div>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-xs text-brand-900/50">{t.categories.note}</p>
      </div>
    </div>
  </section>
);

const ContactRow = ({ icon: Icon, label, value, href, external, testId }) => {
  const Wrap = href ? "a" : "div";
  const props = href ? { href, ...(external ? { target: "_blank", rel: "noreferrer" } : {}), "data-testid": testId } : {};
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
        <Icon className="h-4 w-4 text-brand-500" />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-brand-900/50">{label}</div>
        <Wrap {...props} className={`text-sm font-semibold text-brand-900 ${href ? "hover:text-brand-500" : ""}`}>{value}</Wrap>
      </div>
    </div>
  );
};

/* ---------------- Services ---------------- */
const SERVICE_ICONS = [Handshake, ShoppingCart, ShieldCheck, TrendingUp];

const Services = ({ t }) => (
  <section id="servicios" data-testid="services-section" className="relative overflow-hidden bg-brand-900 py-24 sm:py-32">
    <div className="grain absolute inset-0" />
    <div className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
    <div className="pointer-events-none absolute -top-40 -left-20 h-[24rem] w-[24rem] rounded-full bg-brand-700/40 blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">{t.services.kicker}</p>
        <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl">{t.services.title}</h2>
        <div className="mx-auto mt-6 h-[3px] w-16 rounded-full bg-brand-500" />
      </div>
      <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {t.services.items.map(({ title, desc }, i) => {
          const Icon = SERVICE_ICONS[i];
          return (
            <div key={title} data-testid={`service-card-${i}`} className="group flex flex-col items-center text-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-brand-300 transition-all duration-300 group-hover:-translate-y-1 group-hover:border-brand-500 group-hover:bg-brand-500/15 group-hover:text-white">
                <Icon className="h-7 w-7" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">{title}</h3>
              <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-brand-100/70">{desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const BADGE_ICONS = [Globe, BadgeCheck, Users];

const Contact = ({ t }) => {
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", phone: "", country: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error(t.contact.toast_missing);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        country: form.country.trim() || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success(t.contact.toast_ok);
      setForm({ full_name: "", company_name: "", email: "", phone: "", country: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || t.contact.toast_err;
      toast.error(typeof msg === "string" ? msg : t.contact.toast_err);
    } finally {
      setSubmitting(false);
    }
  };

  const f = t.contact.form;

  return (
    <section id="contacto" data-testid="contact-section" className="bg-brand-50/50 py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-10 lg:grid-cols-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-500">{t.contact.kicker}</p>
          <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight text-brand-900 sm:text-5xl">{t.contact.title}</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-brand-900/70 sm:text-lg">{t.contact.body}</p>

          <div className="mt-10 grid grid-cols-3 gap-4">
            {t.contact.badges.map((label, i) => {
              const Icon = BADGE_ICONS[i];
              return (
                <div key={label} className="flex flex-col items-center rounded-2xl border border-brand-100 bg-white p-4 text-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50">
                    <Icon className="h-5 w-5 text-brand-500" />
                  </div>
                  <div className="text-xs font-semibold leading-snug text-brand-900">{label}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col gap-3">
            <a href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} data-testid="phone-cta" className="inline-flex items-center gap-3 rounded-xl border border-brand-100 bg-white px-4 py-3 text-sm font-semibold text-brand-900 transition-colors hover:border-brand-300 hover:bg-brand-50">
              <Phone className="h-4 w-4 text-brand-500" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="email-cta" className="inline-flex items-center gap-3 rounded-xl border border-brand-100 bg-white px-4 py-3 text-sm font-semibold text-brand-900 transition-colors hover:border-brand-300 hover:bg-brand-50">
              <Mail className="h-4 w-4 text-brand-500" /> {CONTACT.email}
            </a>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="rounded-3xl border border-brand-100 bg-white p-6 shadow-2xl shadow-brand-500/10 sm:p-10">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={f.name} testId="contact-input-name" required value={form.full_name} onChange={update("full_name")} placeholder={f.name_ph} />
            <Field label={f.company} testId="contact-input-company" value={form.company_name} onChange={update("company_name")} placeholder={f.company_ph} />
            <Field label={f.email} testId="contact-input-email" required type="email" value={form.email} onChange={update("email")} placeholder={f.email_ph} />
            <Field label={f.phone} testId="contact-input-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder={f.phone_ph} />
            <div className="sm:col-span-2">
              <Field label={f.country} testId="contact-input-country" value={form.country} onChange={update("country")} placeholder={f.country_ph} />
            </div>
            <div className="flex flex-col sm:col-span-2">
              <label className="mb-1.5 text-xs font-bold uppercase tracking-widest text-brand-900/60">{f.message}</label>
              <textarea data-testid="contact-input-message" required value={form.message} onChange={update("message")} rows={5} className="resize-none rounded-xl border border-brand-100 bg-white px-4 py-3 text-sm text-brand-900 outline-none transition-all placeholder:text-brand-900/30 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder={f.message_ph} />
            </div>
          </div>
          <button data-testid="contact-submit-button" type="submit" disabled={submitting} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition-all duration-300 hover:bg-brand-600 hover:shadow-xl hover:shadow-brand-500/40 disabled:cursor-not-allowed disabled:opacity-60">
            {submitting ? f.sending : f.submit}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>
          <p className="mt-3 text-center text-xs text-brand-900/50">{f.privacy}</p>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, testId, required, type = "text", value, onChange, placeholder }) => (
  <div className="flex flex-col">
    <label className="mb-1.5 text-xs font-bold uppercase tracking-widest text-brand-900/60">{label}</label>
    <input data-testid={testId} required={required} type={type} value={value} onChange={onChange} placeholder={placeholder} className="rounded-xl border border-brand-100 bg-white px-4 py-3 text-sm text-brand-900 outline-none transition-all placeholder:text-brand-900/30 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" />
  </div>
);

/* ---------------- Footer ---------------- */
const Footer = ({ lang, t }) => {
  const NAV_LINKS = [
    { label: t.nav.home, href: "#inicio" },
    { label: t.nav.about, href: "#nosotros" },
    { label: t.nav.services, href: "#servicios" },
  ];
  const city = lang === "es" ? CONTACT.city_es : CONTACT.city_en;
  return (
    <footer data-testid="footer" className="relative overflow-hidden bg-brand-900 text-white">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          <div>
            <Logo variant="white" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-brand-100/70">
              {lang === "es"
                ? "Distribuimos productos de alta calidad a través de una red confiable, aliándonos con marcas y proveedores comprometidos con la excelencia."
                : "We distribute high-quality products through a reliable network, partnering with brands and suppliers committed to excellence."}
            </p>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">{t.footer.nav}</div>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}><a href={l.href} className="text-sm text-brand-100/80 hover:text-white">{l.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">{t.footer.contact}</div>
            <ul className="mt-5 space-y-3 text-sm text-brand-100/80">
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-300" /> {city}</li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-300" /><a href={`mailto:${CONTACT.email}`} className="hover:text-white">{CONTACT.email}</a></li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-300" /><a href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`} className="hover:text-white">{CONTACT.phone}</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-16 border-t border-white/10 pt-8 text-center">
          <div className="font-display text-6xl font-black tracking-tight text-white/10 sm:text-8xl lg:text-[10rem] lowercase">wonderbox</div>
        </div>
        <div className="mt-6 flex flex-col items-center justify-between gap-3 text-xs text-brand-100/60 sm:flex-row">
          <div>© {new Date().getFullYear()} Wonderbox. {t.footer.rights}</div>
          <div>{t.footer.tagline} {city}.</div>
        </div>
      </div>
    </footer>
  );
};

/* ---------------- App ---------------- */
function App() {
  const [lang, setLang] = useState("en");
  const t = useMemo(() => TRANSLATIONS[lang], [lang]);
  return (
    <div className="App min-h-screen bg-white antialiased">
      <Toaster richColors position="top-right" />
      <Header lang={lang} setLang={setLang} t={t} />
      <main>
        <Hero t={t} />
        <Partners t={t} lang={lang} />
        <AboutAndCategories lang={lang} t={t} />
        <Services t={t} />
        <Contact t={t} />
      </main>
      <Footer lang={lang} t={t} />
    </div>
  );
}

export default App;
